import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import styled from '@mui/material/styles/styled';
import { TBL_HR_FEEDBACK } from '../../../../constants/tblHrFeedback';

// Styled components
const PaperCard = styled(Box)(({ theme, background }) => ({
    borderRadius: '20px',
    padding: '20px',
    boxShadow: theme.palette.shadows?.custom?.card || '0px 2px 8px rgba(0, 0, 0, 0.1)',
    border: 'none',
    position: 'relative',
    background: background || theme.palette.figma.block,
}));

const StressIndexCard = ({
    physicalRecord = {},
    sx = {},
    onClick = () => { },
    background = null
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const progressBarRef = useRef(null);
    const [progressBarWidth, setProgressBarWidth] = useState(0);

    const stressLevel = physicalRecord?.stressLevel ?? null;
    const stressLevelData = TBL_HR_FEEDBACK[stressLevel];

    // 프로그레스 바 너비 측정
    useEffect(() => {
        const updateWidth = () => {
            if (progressBarRef.current) {
                const width = progressBarRef.current.offsetWidth;
                setProgressBarWidth(width);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);

        // ResizeObserver를 사용하여 더 정확한 측정
        const resizeObserver = new ResizeObserver(updateWidth);
        if (progressBarRef.current) {
            resizeObserver.observe(progressBarRef.current);
        }

        return () => {
            window.removeEventListener('resize', updateWidth);
            resizeObserver.disconnect();
        };
    }, []);

    // 랜덤하게 메시지 선택: 상수는 원문 유지, level로 번역 키 조회 → 없으면 원문 폴백
    const stressMessage = useMemo(() => {
        if (!stressLevelData) return t('loading_data');
        const messageNum = Math.floor(Math.random() * 5) + 1; // 1~5
        const messageKey = `hr_feedback_level${stressLevel}_message${messageNum}`;
        const translated = t(messageKey);
        if (translated === messageKey) {
            const origKey = `message${messageNum}`;
            return stressLevelData[origKey] || messageKey;
        }
        return translated;
    }, [t, stressLevel, stressLevelData]);

    // 인디케이터 위치 및 크기 계산
    // stressLevel: 0(데이터 없음), 1(편안함), 2(낮음), 3(보통), 4(높음), 5(매우 높음)
    const validStressLevel = stressLevel !== null && stressLevel !== undefined ? stressLevel : 0;

    // 인디케이터 위치 계산을 위한 퍼센트 (0-100%)
    // 각 구간의 가운데에 위치하도록 설정
    // 0: 가장 왼쪽 (0%)
    // 1: 편안함 구간 가운데 (10%)
    // 2: 낮음 구간 가운데 (30%)
    // 3: 보통 구간 가운데 (50%)
    // 4: 높음 구간 가운데 (70%)
    // 5: 매우 높음 구간 가운데 (90%)
    const getStressIndexPercent = (level) => {
        switch (level) {
            case 0:
                return 0; // 가장 왼쪽
            case 1:
                return 10; // 편안함 구간 가운데 (0-20%의 가운데)
            case 2:
                return 30; // 낮음 구간 가운데 (20-40%의 가운데)
            case 3:
                return 50; // 보통 구간 가운데 (40-60%의 가운데)
            case 4:
                return 70; // 높음 구간 가운데 (60-80%의 가운데)
            case 5:
                return 90; // 매우 높음 구간 가운데 (80-100%의 가운데)
            default:
                return 0;
        }
    };

    const stressIndexPercent = getStressIndexPercent(validStressLevel);

    // 인디케이터 크기는 고정 (30px)
    const indicatorSize = 30;
    const indicatorRadius = indicatorSize / 2;

    // 인디케이터 위치 계산 (프로그레스 바 너비에 따라 동적으로 계산)
    // 인디케이터의 중심 위치 계산
    const indicatorCenterPosition = progressBarWidth > 0
        ? (stressIndexPercent / 100) * progressBarWidth
        : 0;
    // 인디케이터의 왼쪽 가장자리 위치 (중심에서 반지름을 뺌)
    const indicatorLeftPosition = indicatorCenterPosition - indicatorRadius;
    // 인디케이터가 경계를 넘지 않도록 제한
    const maxLeftPosition = progressBarWidth > 0 ? progressBarWidth - indicatorSize : 0;
    const clampedPosition = Math.min(Math.max(indicatorLeftPosition, 0), maxLeftPosition);

    return (
        <PaperCard sx={sx} background={background} onClick={onClick}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'flex-start',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {/* Title */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '18px',
                                fontWeight: 500,
                                letterSpacing: '-0.54px',
                            }}
                        >
                            {t('stress_index')}
                        </Typography>
                    </Box>

                    {/* Progress Bar Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '5px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px 0',
                            width: '100%',
                        }}
                    >
                        {/* Labels */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                gap: 0,
                            }}
                        >
                            <Typography
                                sx={{
                                    flex: 1,
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    textAlign: 'center',
                                    letterSpacing: '-0.36px',
                                }}
                            >
                                {t('relaxed')}
                            </Typography>
                            <Typography
                                sx={{
                                    flex: 1,
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    textAlign: 'center',
                                    letterSpacing: '-0.36px',
                                }}
                            >
                                {t('low')}
                            </Typography>
                            <Typography
                                sx={{
                                    flex: 1,
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    textAlign: 'center',
                                    letterSpacing: '-0.36px',
                                }}
                            >
                                {t('normal')}
                            </Typography>
                            <Typography
                                sx={{
                                    flex: 1,
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    textAlign: 'center',
                                    letterSpacing: '-0.36px',
                                }}
                            >
                                {t('high')}
                            </Typography>
                            <Typography
                                sx={{
                                    flex: 1,
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    textAlign: 'center',
                                    letterSpacing: '-0.36px',
                                }}
                            >
                                {t('very_high')}
                            </Typography>
                        </Box>

                        {/* Progress Bar Container */}
                        <Box
                            ref={progressBarRef}
                            sx={{
                                height: '30px',
                                position: 'relative',
                                width: '100%',
                                borderRadius: '10px',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Background Bars */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    height: '100%',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: '100%',
                                        backgroundColor: '#00BFFF',
                                        borderRadius: '33px 0 0 33px',
                                    }}
                                />
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: '100%',
                                        backgroundColor: '#76f112',
                                    }}
                                />
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: '100%',
                                        backgroundColor: '#ffb200',
                                    }}
                                />
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: '100%',
                                        backgroundColor: '#ff5900',
                                    }}
                                />
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: '100%',
                                        backgroundColor: '#d90000',
                                        borderRadius: '0 33px 33px 0',
                                    }}
                                />
                            </Box>

                            {/* Indicator */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: `${clampedPosition}px`,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '32px',
                                    backgroundColor: theme.palette.figma.iconWhite,
                                    zIndex: 10,
                                    boxShadow: theme.palette.shadows?.elevation?.[1] ?? '0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Message */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: theme.palette.figma.white[80],
                            textAlign: 'center',
                            letterSpacing: '-0.42px',
                            lineHeight: 'normal',
                            wordBreak: 'keep-all',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                        }}
                    >
                        {stressMessage}
                    </Typography>
                </Box>
            </Box>
        </PaperCard>
    );
};

export default StressIndexCard;

